/**
 * Registration Session Service
 * 
 * Handles C1 registration session recovery:
 * - Start new or resume existing sessions
 * - Save progress at each step
 * - Resume sessions on page load
 * - Clean up after successful activation
 * 
 * This service communicates with the backend API for persistence.
 * Session token is stored in HttpOnly cookie by the backend.
 * 
 * In mock/dev mode, uses localStorage for session persistence.
 */

import { C1SignupData } from '../components/C1Signup/C1SignupFlow';
import { logger } from '../utils/logger';
import { getRuntimeConfig } from '../config/runtime';

// ─── Configuration ──────────────────────────────────────────────────────────

/**
 * Check if running in mock mode (no backend)
 */
function isMockMode(): boolean {
  const runtimeConfig = getRuntimeConfig();
  // Check runtime config mode
  if (runtimeConfig?.mode === 'mock') return true;
  // Check if demo login
  if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('isDemoLogin') === 'true') return true;
  // Check for localhost without explicit prod config
  if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
    // If we're on localhost and mode is not explicitly 'prod', use mock
    return runtimeConfig?.mode !== 'prod';
  }
  return false;
}

/**
 * Get the registration API URL from runtime config or environment
 */
function getRegistrationApiUrl(): string {
  const runtimeConfig = getRuntimeConfig();
  return runtimeConfig?.VITE_REGISTRATION_API_URL ||
    (import.meta.env.VITE_REGISTRATION_API_URL as string) || 
    'https://ppu7ft7aqe.execute-api.ap-southeast-2.amazonaws.com/dev1';
}

// Local storage key for session token fallback (if cookies fail)
const SESSION_TOKEN_KEY = 'reg_session_token';

// Local storage key for mock session data
const MOCK_SESSION_KEY = 'mock_registration_session';

// ─── Types ──────────────────────────────────────────────────────────────────

export type RegistrationStep = 
  | 'party-details' 
  | 'review' 
  | 'billing' 
  | 'payment-details' 
  | 'agreement-sign'
  | 'austrac-docs'
  | 'go-live' 
  | 'complete';

export interface RegistrationSession {
  sessionToken: string;
  email: string;
  currentStep: RegistrationStep;
  businessDetails: Partial<C1SignupData>;
  reviewSign: Partial<C1SignupData>;
  planSelection: Partial<C1SignupData>;
  paymentDetails: Partial<C1SignupData>;
  isResumed: boolean;
  expiresAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface StartSessionResponse {
  success: boolean;
  session?: RegistrationSession;
  error?: string;
}

export interface SaveProgressResponse {
  success: boolean;
  currentStep?: RegistrationStep;
  error?: string;
}

export interface ResumeSessionResponse {
  found: boolean;
  session?: RegistrationSession;
  error?: string;
}

// ─── Helper Functions ───────────────────────────────────────────────────────

/**
 * Get session token from localStorage (fallback for when cookies don't work)
 */
function getStoredSessionToken(): string | null {
  try {
    return localStorage.getItem(SESSION_TOKEN_KEY);
  } catch {
    return null;
  }
}

/**
 * Store session token in localStorage (fallback)
 */
function storeSessionToken(token: string): void {
  try {
    localStorage.setItem(SESSION_TOKEN_KEY, token);
  } catch {
    logger.warn('Failed to store session token in localStorage');
  }
}

/**
 * Clear session token from localStorage
 */
function clearStoredSessionToken(): void {
  try {
    localStorage.removeItem(SESSION_TOKEN_KEY);
  } catch {
    logger.warn('Failed to clear session token from localStorage');
  }
}

/**
 * Make API request with credentials (for cookie handling)
 */
async function apiRequest<T>(
  endpoint: string,
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE',
  body?: Record<string, unknown>
): Promise<T> {
  const baseUrl = getRegistrationApiUrl();
  const url = `${baseUrl}${endpoint}`;
  
  logger.info(`[RegistrationSession] API request: ${method} ${url}`);
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  
  // Include session token in header as fallback for environments without cookies
  const storedToken = getStoredSessionToken();
  if (storedToken) {
    headers['X-Registration-Session'] = storedToken;
  }
  
  const response = await fetch(url, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `API request failed: ${response.status}`);
  }
  
  return response.json();
}

// ─── API Functions ──────────────────────────────────────────────────────────

/**
 * Start a new registration session or resume existing one
 * 
 * Called when user submits their email on Step 1.
 * Backend sets HttpOnly cookie with session_token.
 * 
 * @param email - User's email address
 * @returns Session data with existing progress if resuming
 */
export async function startRegistrationSession(email: string): Promise<StartSessionResponse> {
  try {
    logger.info('[RegistrationSession] Starting session', { email });
    
    const response = await apiRequest<{
      session_token: string;
      current_step: RegistrationStep;
      business_details: Partial<C1SignupData>;
      review_sign: Partial<C1SignupData>;
      plan_selection: Partial<C1SignupData>;
      payment_details: Partial<C1SignupData>;
      is_resumed: boolean;
      expires_at: string;
      created_at: string;
      updated_at: string;
      email: string;
    }>('/registration/start', 'POST', { email });
    
    // Store token in localStorage as fallback
    if (response.session_token) {
      storeSessionToken(response.session_token);
    }
    
    const session: RegistrationSession = {
      sessionToken: response.session_token,
      email: response.email || email,
      currentStep: response.current_step || 'party-details',
      businessDetails: response.business_details || {},
      reviewSign: response.review_sign || {},
      planSelection: response.plan_selection || {},
      paymentDetails: response.payment_details || {},
      isResumed: response.is_resumed || false,
      expiresAt: response.expires_at,
      createdAt: response.created_at,
      updatedAt: response.updated_at,
    };
    
    logger.info('[RegistrationSession] Session started', { 
      isResumed: session.isResumed,
      currentStep: session.currentStep 
    });
    
    return { success: true, session };
  } catch (error) {
    logger.error('[RegistrationSession] Failed to start session', { error });
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Failed to start registration session' 
    };
  }
}

/**
 * Save progress for a specific step
 * 
 * Called on each step completion (Next button click).
 * Also can be called on field blur for progressive autosave.
 * 
 * @param step - The step being saved (data will be stored under this step's section)
 * @param data - The form data for this step
 * @param nextStep - Optional next step to set as currentStep (defaults to step)
 * @returns Updated current step
 */
export async function saveRegistrationProgress(
  step: RegistrationStep,
  data: Partial<C1SignupData>,
  nextStep?: RegistrationStep
): Promise<SaveProgressResponse> {
  try {
    logger.info('[RegistrationSession] Saving progress', { step, nextStep });
    
    const response = await apiRequest<{
      saved: boolean;
      current_step: RegistrationStep;
    }>('/registration/save', 'PATCH', { step, data, nextStep });
    
    logger.info('[RegistrationSession] Progress saved', { 
      currentStep: response.current_step 
    });
    
    return { 
      success: response.saved, 
      currentStep: response.current_step 
    };
  } catch (error) {
    logger.error('[RegistrationSession] Failed to save progress', { error, step });
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Failed to save progress' 
    };
  }
}

/**
 * Resume an existing registration session
 * 
 * Called on page load of /signup to check for existing session.
 * Uses HttpOnly cookie first, falls back to email lookup.
 * 
 * @param email - Optional email for lookup if no cookie
 * @returns Session data if found
 */
export async function resumeRegistrationSession(email?: string): Promise<ResumeSessionResponse> {
  try {
    logger.info('[RegistrationSession] Attempting to resume session');
    
    const queryParams = email ? `?email=${encodeURIComponent(email)}` : '';
    
    const response = await apiRequest<{
      found: boolean;
      current_step?: RegistrationStep;
      business_details?: Partial<C1SignupData>;
      review_sign?: Partial<C1SignupData>;
      plan_selection?: Partial<C1SignupData>;
      payment_details?: Partial<C1SignupData>;
      session_token?: string;
      email?: string;
      expires_at?: string;
      created_at?: string;
      updated_at?: string;
    }>(`/registration/resume${queryParams}`, 'GET');
    
    if (!response.found) {
      logger.info('[RegistrationSession] No session found');
      return { found: false };
    }
    
    // Store token in localStorage as fallback
    if (response.session_token) {
      storeSessionToken(response.session_token);
    }
    
    const session: RegistrationSession = {
      sessionToken: response.session_token || '',
      email: response.email || email || '',
      currentStep: response.current_step || 'party-details',
      businessDetails: response.business_details || {},
      reviewSign: response.review_sign || {},
      planSelection: response.plan_selection || {},
      paymentDetails: response.payment_details || {},
      isResumed: true,
      expiresAt: response.expires_at || '',
      createdAt: response.created_at || '',
      updatedAt: response.updated_at || '',
    };
    
    logger.info('[RegistrationSession] Session resumed', { 
      currentStep: session.currentStep,
      email: session.email 
    });
    
    return { found: true, session };
  } catch (error) {
    logger.error('[RegistrationSession] Failed to resume session', { error });
    // Don't throw - just return not found
    return { found: false };
  }
}

/**
 * Delete registration session after successful activation
 * 
 * Called by Stripe webhook handler after payment confirmed.
 * Also clears the HttpOnly cookie.
 */
export async function deleteRegistrationSession(): Promise<boolean> {
  try {
    logger.info('[RegistrationSession] Deleting session');
    
    await apiRequest('/registration/session', 'DELETE');
    
    // Clear localStorage fallback
    clearStoredSessionToken();
    
    logger.info('[RegistrationSession] Session deleted');
    return true;
  } catch (error) {
    logger.error('[RegistrationSession] Failed to delete session', { error });
    return false;
  }
}

/**
 * Map internal step names to API step names
 */
export function mapStepToApiStep(step: string): RegistrationStep {
  const stepMap: Record<string, RegistrationStep> = {
    'party-details': 'party-details',
    'review': 'review',
    'billing': 'billing',
    'payment-details': 'payment-details',
    'agreement-sign': 'agreement-sign',
    'austrac-docs': 'austrac-docs',
    'go-live': 'go-live',
    'complete': 'complete',
  };
  return stepMap[step] || 'party-details';
}

/**
 * Merge saved session data with current signup data
 */
export function mergeSessionData(
  currentData: C1SignupData,
  session: RegistrationSession
): C1SignupData {
  return {
    ...currentData,
    ...session.businessDetails,
    ...session.reviewSign,
    ...session.planSelection,
    ...session.paymentDetails,
  };
}

/**
 * Extract step-specific data from signup data
 */
export function extractStepData(
  step: RegistrationStep,
  data: C1SignupData
): Partial<C1SignupData> {
  switch (step) {
    case 'party-details':
      return {
        partyName: data.partyName,
        partyAbn: data.partyAbn,
        partyAcn: data.partyAcn,
        partyEmail: data.partyEmail,
        partyEntityType: data.partyEntityType,
        partyAddress: data.partyAddress,
        partySector: data.partySector,
        adminPhone: data.adminPhone,
      };
    case 'review':
      return {
        reviewConfirmed: data.reviewConfirmed,
        signatureData: data.signatureData,
        agreedToTerms: data.agreedToTerms,
        signatureAccepted: data.signatureAccepted,
        signatureMode: data.signatureMode,
        drawnSignature: data.drawnSignature,
      };
    case 'billing':
      return {
        stripePlanType: data.stripePlanType,
        billingCycle: data.billingCycle,
        paymentMethod: data.paymentMethod,
        creditsPurchased: data.creditsPurchased,
        totalAmount: data.totalAmount,
      };
    case 'payment-details':
      return {
        stripePaymentMethodId: data.stripePaymentMethodId,
        stripeBillingEmail: data.stripeBillingEmail,
      };
    default:
      return {};
  }
}
